import { useEffect } from "react";
import { useIntl } from "react-intl";
import {
    Alert, Button, Card, Col, Form, Input, InputNumber, Row, Select, Space, Spin, Switch, Tag,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cardmargin, grayheader } from "../../CSSReferences";
import {
    AUTH_PROVIDERS, AuthProvider, AuthSecrets, AuthSettings, ConnectionKind, KeycloakField,
    LdapField, PROVISION_ROLES, ProvisionRole, TestResult,
} from "../typing/generalType";
import {
    isKeycloakActive, isLdapActive, judgeProviders, moveProvider, toggleProvider,
} from "../typing/authSettingsLogic";
import { AuthSettingsState } from "../typing/settingsstate";

/** Role number of an administrator in the login token (DbEnum.AccessRole.Admin). */
const ADMIN_ROLE = 2;

export interface AuthSettingsViewProps {
    id: string;
    settings: AuthSettings;
    secrets: AuthSecrets;
    status: AuthSettingsState["view"];
    role: number;
    onLoadRequested: () => void;
    onProvidersChange: (providers: readonly AuthProvider[]) => void;
    onProvisionRoleChange: (role: ProvisionRole) => void;
    onLdapChange: (field: LdapField, value: string | number | boolean | null) => void;
    onKeycloakChange: (field: KeycloakField, value: string | boolean) => void;
    onSecretChange: (field: keyof AuthSecrets, value: string) => void;
    onSave: (settings: AuthSettings, secrets: AuthSecrets, savedText: string) => void;
    onTest: (kind: ConnectionKind) => void;
}

type Translate = (id: string) => string;

/**
 * Settings page for how people log in to the hub: the order of login methods,
 * LDAP / Active Directory, Keycloak and the role of provisioned accounts.
 *
 * Admin only. The hub enforces that on every call; the page only avoids
 * showing an Admin form, and asking the hub, for anybody else.
 */
export function AuthSettingsView(props: AuthSettingsViewProps) {
    const intl = useIntl();
    const t: Translate = (id) => intl.formatMessage({ id, defaultMessage: id });
    const isAdmin = props.role === ADMIN_ROLE;
    const { onLoadRequested } = props;

    useEffect(() => {
        if (isAdmin) onLoadRequested();
    }, [isAdmin, onLoadRequested]);

    if (!isAdmin) {
        return <Alert type="warning" showIcon title={t("AuthSettings:AdminOnly")} style={cardmargin} />;
    }

    if (!props.status.loaded) {
        return props.status.loadFailed
            ? <Alert type="error" showIcon title={t("AuthSettings:LoadFailed")} style={cardmargin}
                     action={<Button onClick={onLoadRequested}>{t("AuthSettings:Retry")}</Button>} />
            : <Spin style={{ display: "block", margin: "10vh auto" }} />;
    }

    const { settings, secrets, status } = props;

    return (
        <Form layout="vertical" component="div">
            <ProvidersCard t={t} providers={settings.providers} onChange={props.onProvidersChange} />
            <Row gutter={16}>
                <Col xs={24} xl={12}>
                    <LdapCard t={t} settings={settings} secret={secrets.bindPassword}
                              test={status.ldapTest} onChange={props.onLdapChange}
                              onSecretChange={(value) => props.onSecretChange("bindPassword", value)}
                              onTest={() => props.onTest("ldap")} />
                </Col>
                <Col xs={24} xl={12}>
                    <KeycloakCard t={t} settings={settings} secret={secrets.clientSecret}
                                  test={status.keycloakTest} onChange={props.onKeycloakChange}
                                  onSecretChange={(value) => props.onSecretChange("clientSecret", value)}
                                  onTest={() => props.onTest("keycloak")} />
                </Col>
            </Row>
            <Card title={t("AuthSettings:ProvisionTitle")} style={cardmargin} styles={{ header: grayheader }}>
                <Form.Item label={t("AuthSettings:ProvisionRole")} extra={t("AuthSettings:ProvisionRoleHint")}>
                    <Select<ProvisionRole>
                        style={{ maxWidth: 240 }}
                        value={settings.provisionRole}
                        onChange={props.onProvisionRoleChange}
                        options={PROVISION_ROLES.map((role) => ({ value: role, label: t("AuthSettings:Role:" + role) }))} />
                </Form.Item>
            </Card>
            <Card style={cardmargin}>
                {status.errors.length > 0 &&
                    <Alert type="error" showIcon style={{ marginBottom: 16 }}
                           title={t("AuthSettings:SaveRefused")}
                           description={<ul style={{ margin: 0 }}>{status.errors.map((e) => <li key={e}>{e}</li>)}</ul>} />}
                <Button type="primary" loading={status.saving}
                        icon={<FontAwesomeIcon icon={["fas", "floppy-disk"]} />}
                        onClick={() => props.onSave(settings, secrets, t("AuthSettings:Saved"))}>
                    {t("AuthSettings:Save")}
                </Button>
            </Card>
        </Form>
    );
}

// --- Login methods ------------------------------------------------------------

function ProvidersCard(props: {
    t: Translate;
    providers: readonly AuthProvider[];
    onChange: (providers: readonly AuthProvider[]) => void;
}) {
    const { t, providers, onChange } = props;
    const verdict = judgeProviders(providers);
    const available = AUTH_PROVIDERS.filter((p) => !providers.includes(p));

    return (
        <Card title={t("AuthSettings:MethodsTitle")} style={cardmargin} styles={{ header: grayheader }}>
            <p>{t("AuthSettings:MethodsHint")}</p>
            {verdict.kind === "localOnly" &&
                <Alert type="info" showIcon title={t("AuthSettings:LocalOnly")} style={{ marginBottom: 12 }} />}
            {verdict.kind === "noLocal" &&
                <Alert type="warning" showIcon title={t("AuthSettings:NoLocalWarning")} style={{ marginBottom: 12 }} />}
            <ol style={{ paddingLeft: 20 }}>
                {providers.map((provider, index) => (
                    <li key={provider} style={{ marginBottom: 6 }}>
                        <Space>
                            <span style={{ display: "inline-block", minWidth: 180 }}>
                                {t("AuthSettings:Method:" + provider)}
                            </span>
                            <Button size="small" disabled={index === 0} title={t("AuthSettings:Up")}
                                    icon={<FontAwesomeIcon icon={["fas", "arrow-up"]} />}
                                    onClick={() => onChange(moveProvider(providers, index, -1))} />
                            <Button size="small" disabled={index === providers.length - 1} title={t("AuthSettings:Down")}
                                    icon={<FontAwesomeIcon icon={["fas", "arrow-down"]} />}
                                    onClick={() => onChange(moveProvider(providers, index, 1))} />
                            <Button size="small" danger disabled={providers.length === 1} title={t("AuthSettings:Remove")}
                                    icon={<FontAwesomeIcon icon={["fas", "xmark"]} />}
                                    onClick={() => onChange(toggleProvider(providers, provider, false))} />
                        </Space>
                    </li>
                ))}
            </ol>
            {available.length > 0 &&
                <Space wrap>
                    <span>{t("AuthSettings:Add")}</span>
                    {available.map((provider) => (
                        <Button key={provider} size="small"
                                icon={<FontAwesomeIcon icon={["fas", "plus"]} />}
                                onClick={() => onChange(toggleProvider(providers, provider, true))}>
                            {t("AuthSettings:Method:" + provider)}
                        </Button>
                    ))}
                </Space>}
        </Card>
    );
}

// --- Shared pieces --------------------------------------------------------------

function SectionTitle(props: { t: Translate; title: string; active: boolean }) {
    return (
        <Space>
            {props.title}
            {props.active
                ? <Tag color="green">{props.t("AuthSettings:Active")}</Tag>
                : <Tag>{props.t("AuthSettings:Inactive")}</Tag>}
        </Space>
    );
}

function SecretField(props: {
    t: Translate; label: string; isSet: boolean; value: string; onChange: (value: string) => void;
}) {
    const { t } = props;
    return (
        <Form.Item label={props.label} extra={t("AuthSettings:SecretHint")}>
            <Space orientation="vertical" style={{ width: "100%" }}>
                {props.isSet
                    ? <Tag color="green">{t("AuthSettings:SecretSet")}</Tag>
                    : <Tag>{t("AuthSettings:SecretNotSet")}</Tag>}
                <Input.Password autoComplete="new-password" value={props.value}
                                placeholder={t("AuthSettings:SecretPlaceholder")}
                                onChange={(e) => props.onChange(e.target.value)} />
            </Space>
        </Form.Item>
    );
}

function TestButton(props: { t: Translate; test: TestResult; onTest: () => void }) {
    const { t, test } = props;
    return (
        <Form.Item extra={t("AuthSettings:TestHint")}>
            <Space orientation="vertical" style={{ width: "100%" }}>
                <Button loading={test.kind === "running"} onClick={props.onTest}
                        icon={<FontAwesomeIcon icon={["fas", "plug"]} />}>
                    {t("AuthSettings:Test")}
                </Button>
                {test.kind === "done" &&
                    <Alert type={test.ok ? "success" : "error"} showIcon
                           title={test.ok ? t("AuthSettings:TestOk") : t("AuthSettings:TestFailed")}
                           description={test.message || undefined} />}
            </Space>
        </Form.Item>
    );
}

// Inactive sections stay editable, but are dimmed so nobody takes them for
// what the hub uses right now.
const sectionStyle = (active: boolean) => active ? cardmargin : { ...cardmargin, opacity: 0.7 };

// --- LDAP / Active Directory ------------------------------------------------------

function LdapCard(props: {
    t: Translate;
    settings: AuthSettings;
    secret: string;
    test: TestResult;
    onChange: (field: LdapField, value: string | number | boolean | null) => void;
    onSecretChange: (value: string) => void;
    onTest: () => void;
}) {
    const { t, onChange } = props;
    const ldap = props.settings.ldap;
    const active = isLdapActive(props.settings.providers);
    const text = (field: LdapField, label: string, extra?: string, placeholder?: string) => (
        <Form.Item label={t(label)} extra={extra && t(extra)}>
            <Input value={ldap[field] as string} placeholder={placeholder}
                   onChange={(e) => onChange(field, e.target.value)} />
        </Form.Item>
    );

    return (
        <Card title={<SectionTitle t={t} title={t("AuthSettings:LdapTitle")} active={active} />}
              style={sectionStyle(active)} styles={{ header: grayheader }}>
            {text("server", "AuthSettings:Ldap:Server", undefined, "dc01.example.local")}
            <Form.Item label={t("AuthSettings:Ldap:Port")} extra={t("AuthSettings:Ldap:PortHint")}>
                <InputNumber min={1} max={65535} style={{ width: 160 }} value={ldap.port}
                             onChange={(value) => onChange("port", typeof value === "number" ? value : null)} />
            </Form.Item>
            {text("baseDn", "AuthSettings:Ldap:BaseDn", undefined, "DC=example,DC=local")}
            {text("bindDn", "AuthSettings:Ldap:BindDn", undefined, "CN=svc-anima,OU=Service,DC=example,DC=local")}
            <SecretField t={t} label={t("AuthSettings:Ldap:BindPassword")} isSet={ldap.bindPasswordSet}
                         value={props.secret} onChange={props.onSecretChange} />
            {text("userAttribute", "AuthSettings:Ldap:UserAttribute", "AuthSettings:Ldap:UserAttributeHint")}
            {text("group", "AuthSettings:Ldap:Group", "AuthSettings:Ldap:GroupHint")}
            <Form.Item label={t("AuthSettings:AutoProvision")} extra={t("AuthSettings:AutoProvisionHint")}>
                <Switch checked={ldap.autoProvision} onChange={(checked) => onChange("autoProvision", checked)} />
            </Form.Item>
            <TestButton t={t} test={props.test} onTest={props.onTest} />
        </Card>
    );
}

// --- Keycloak / OIDC ------------------------------------------------------------

function KeycloakCard(props: {
    t: Translate;
    settings: AuthSettings;
    secret: string;
    test: TestResult;
    onChange: (field: KeycloakField, value: string | boolean) => void;
    onSecretChange: (value: string) => void;
    onTest: () => void;
}) {
    const { t, onChange } = props;
    const keycloak = props.settings.keycloak;
    const active = isKeycloakActive(props.settings.providers);
    const text = (field: KeycloakField, label: string, extra?: string, placeholder?: string) => (
        <Form.Item label={t(label)} extra={extra && t(extra)}>
            <Input value={keycloak[field] as string} placeholder={placeholder}
                   onChange={(e) => onChange(field, e.target.value)} />
        </Form.Item>
    );

    return (
        <Card title={<SectionTitle t={t} title={t("AuthSettings:KeycloakTitle")} active={active} />}
              style={sectionStyle(active)} styles={{ header: grayheader }}>
            {text("url", "AuthSettings:Keycloak:Url", undefined, "https://keycloak.example.local")}
            {text("realm", "AuthSettings:Keycloak:Realm")}
            {text("clientId", "AuthSettings:Keycloak:ClientId")}
            <SecretField t={t} label={t("AuthSettings:Keycloak:ClientSecret")} isSet={keycloak.clientSecretSet}
                         value={props.secret} onChange={props.onSecretChange} />
            {text("role", "AuthSettings:Keycloak:Role", "AuthSettings:Keycloak:RoleHint")}
            {text("redirectUri", "AuthSettings:Keycloak:RedirectUri")}
            {text("scope", "AuthSettings:Keycloak:Scope", undefined, "openid profile email")}
            <Form.Item label={t("AuthSettings:AutoProvision")} extra={t("AuthSettings:AutoProvisionHint")}>
                <Switch checked={keycloak.autoProvision} onChange={(checked) => onChange("autoProvision", checked)} />
            </Form.Item>
            <TestButton t={t} test={props.test} onTest={props.onTest} />
        </Card>
    );
}
